// linkify — turn URLs, email addresses, and phone numbers inside a plain string
// into clickable <a> elements, leaving the rest as text. Used by TextBlock so
// operator copy like "call us at 1-800-555-0100 or email order@acme.com" gets
// live tel:/mailto:/http links without the operator authoring any markup.
//
// Deliberately conservative: we only linkify confident matches (explicit URLs,
// proper-looking emails, phone-like runs of 7+ digits) so ordinary prose with
// stray numbers doesn't sprout links. Returns the original string untouched
// (as a single-element array) when nothing matches.

import type { ReactNode } from "react";

// One combined matcher with named alternatives. Order matters: URL first (so a
// URL containing an @ isn't mis-split as an email), then email, then phone.
//   - URL:   http(s):// or www. up to the next whitespace
//   - email: local@domain.tld
//   - phone: optional +, then 7+ digits with spaces / dashes / dots / parens
const PATTERN = new RegExp(
  [
    "(https?:\\/\\/[^\\s]+|www\\.[^\\s]+)", // url
    "([^\\s@]+@[^\\s@]+\\.[^\\s@]+)", // email
    "(\\+?\\d[\\d\\s().-]{6,}\\d)", // phone (≥8 chars, starts/ends with a digit)
  ].join("|"),
  "gi",
);

/** Strip everything but digits and a leading + for the tel: href. */
function telHref(raw: string): string {
  const trimmed = raw.trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  return "tel:" + plus + trimmed.replace(/[^\d]/g, "");
}

/** Split a string into text + anchor nodes. Pure; safe to call every render. */
export function linkify(input: string): ReactNode[] {
  if (!input) return [input];
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  // Reset lastIndex — PATTERN is a shared global-flag regex.
  PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PATTERN.exec(input)) !== null) {
    const [match, url, email, phone] = m;
    if (m.index > last) out.push(input.slice(last, m.index));

    if (url) {
      const href = url.startsWith("www.") ? "https://" + url : url;
      out.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
          {url}
        </a>,
      );
    } else if (email) {
      out.push(
        <a key={key++} href={"mailto:" + email}>
          {email}
        </a>,
      );
    } else if (phone) {
      out.push(
        <a key={key++} href={telHref(phone)}>
          {phone}
        </a>,
      );
    }
    last = m.index + match.length;
  }
  if (last < input.length) out.push(input.slice(last));
  // No matches → return the original string so callers can render it directly.
  return out.length > 0 ? out : [input];
}
